/**
 * Migration gate — determines whether an account should use the Prest0nRuntime path.
 *
 * R19-gate (INCIDENT-20260818, owner live QA "Interactive 3D Cat Simulation"):
 * DEFAULT-MIGRATED. The old contract required accounts/{id}/config/runtime
 * with migrationEnabled=true AND accounts/{id}/vm/main.assigned|enabled —
 * VM-era relics that contradict the server's universal serverless-first
 * routing (executionRouter.ts: vm/main is a capability, not lane identity)
 * and the org-creation contract, which deliberately writes NEITHER doc
 * ("a created org is structurally serverless", orgCreation.ts). Result: the
 * Aug 17 cleanup sweep's newly-constituted org failed the gate and every
 * turn silently rode the abandoned LEGACY in-browser orchestrator — no
 * artifact pipeline, no task lane, 120s/180s proxy guards producing ~3-min
 * "Something went wrong" bubbles, and capability confabulations.
 *
 * New contract — a RESOLVED account rides the prest0n-session lane unless an
 * EXPLICIT negative says otherwise:
 *   Gate 0: global kill switch (system/prest0Core/config/main.vmRoutingEnabled=false)
 *   Gate 1: accounts/{accountId}/config/runtime.migrationEnabled === false
 *           (the lifecycle stop-flip; allowlist may override)
 *   Catch:  permission-denied (access revocation) stays an authoritative negative.
 * A missing config doc, a missing flag, and a missing vm/main are all
 * MIGRATED. Generic read errors default migrated too (sticky cache first) —
 * the legacy orchestrator is the strictly-worse lane and must never be the
 * error fallback.
 *
 * The function also resolves the full AccountResolution so callers don't need to call
 * resolveAccount() a second time.
 */

import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { MIGRATED_ACCOUNT_IDS, ACCOUNT_FIRESTORE_PREFIX } from './constants';
import { resolveAccount, resolveAccountStrict, resolveNamedWorkspaceStrict } from './accountResolver';
import {
  resolveTabWorkspace,
  writeTabWorkspacePin,
  clearTabWorkspacePin,
} from './tabWorkspace';
import {
  clearConfirmedWorkspace,
  readConfirmedWorkspace,
  writeConfirmedWorkspace,
} from './confirmedWorkspaceCache';
import { fernandoTrace, isFernandoStrictMode, FERN_FE_CODES } from './fernandoTrace';
import type { MigrationGateResult, AccountResolution } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Extended gate result that includes the AccountResolution
// ─────────────────────────────────────────────────────────────────────────────

export interface MigrationGateResultWithResolution extends MigrationGateResult {
  /** Full resolution returned so callers avoid a redundant Firestore fetch */
  resolution: AccountResolution | null;
  /**
   * ORG STICKINESS (2026-08-18, INCIDENT: the uid-as-identity fallback):
   * where `accountId` came from.
   *   'resolved'        — a strict resolution succeeded this call;
   *   'cached-explicit' — resolution failed transiently; this is the LAST
   *                       CONFIRMED workspace (confirmedWorkspaceCache) —
   *                       the org the user was last verifiably in;
   *   'none'            — resolution failed and no confirmed cache exists:
   *                       identity is UNRESOLVED (accountId is null) and the
   *                       caller must hold, never guess.
   * Absent on paths that predate the field (accountId was caller-supplied
   * and already resolved).
   */
  accountIdSource?: 'resolved' | 'cached-explicit' | 'tab-pin' | 'none';
}

// ─────────────────────────────────────────────────────────────────────────────
// Sticky-positive migration cache (Round J B1 fix, 2026-07-14)
//
// A TRANSIENT Firestore read error in the catch block used to downgrade a
// previously-migrated account to {isMigrated:false}, silently rerouting the
// turn to the legacy orchestrator branch. Migration status is now sticky:
// once an account has passed the gate, a read ERROR returns the last-known
// positive result (loudly). Explicit negative gate results (kill switch,
// migrationEnabled=false, VM unassigned) remain authoritative and clear the
// cache — only errors are shielded.
// ─────────────────────────────────────────────────────────────────────────────

const STICKY_MIGRATED_LS_PREFIX = 'prest0_migration_sticky_v1:';

/**
 * Finding 6 (2026-07-15): TTL for the sticky-positive entry. Without one, a
 * permanently-broken read path (most notably access revocation after
 * offboarding, which surfaces as permission-denied "errors" forever) kept an
 * account sticky-migrated indefinitely. permission-denied is now ALSO
 * treated as an authoritative negative below; the TTL is the backstop for
 * every other persistent error shape.
 */
export const STICKY_MIGRATED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** In-memory sticky cache: accountId → writtenAt (ms epoch). */
const stickyMigratedAccounts = new Map<string, number>();

function rememberMigrated(accountId: string, nowMs: number = Date.now()): void {
  if (!accountId) return;
  stickyMigratedAccounts.set(accountId, nowMs);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        STICKY_MIGRATED_LS_PREFIX + accountId,
        JSON.stringify({ writtenAt: nowMs }),
      );
    }
  } catch { /* SSR / quota — in-memory cache still holds */ }
}

function forgetMigrated(accountId: string): void {
  if (!accountId) return;
  stickyMigratedAccounts.delete(accountId);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STICKY_MIGRATED_LS_PREFIX + accountId);
    }
  } catch { /* ignore */ }
}

function wasMigratedLastKnown(accountId: string, nowMs: number = Date.now()): boolean {
  if (!accountId) return false;

  const isFresh = (writtenAt: number): boolean =>
    Number.isFinite(writtenAt) && nowMs - writtenAt <= STICKY_MIGRATED_TTL_MS;

  const inMemory = stickyMigratedAccounts.get(accountId);
  if (inMemory !== undefined) {
    if (isFresh(inMemory)) return true;
    stickyMigratedAccounts.delete(accountId);
  }

  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(STICKY_MIGRATED_LS_PREFIX + accountId);
      if (!raw) return false;
      // Legacy Round J format ('1', no timestamp — shipped 2026-07-14):
      // stamp it now so it enters the TTL regime instead of living forever.
      if (raw === '1') {
        rememberMigrated(accountId, nowMs);
        return true;
      }
      const parsed = JSON.parse(raw) as { writtenAt?: number } | null;
      const writtenAt = typeof parsed?.writtenAt === 'number' ? parsed.writtenAt : NaN;
      if (isFresh(writtenAt)) {
        // Refresh the in-memory mirror for cheap subsequent checks.
        stickyMigratedAccounts.set(accountId, writtenAt);
        return true;
      }
      // Expired (or unparseable) → clear so it can't resurrect later.
      localStorage.removeItem(STICKY_MIGRATED_LS_PREFIX + accountId);
    }
  } catch { /* ignore */ }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Permission-denied debounce (2026-08-20, live rail-churn incident)
//
// Finding 6 made permission-denied an INSTANT authoritative negative because
// real revocation (offboarding) surfaces as permission-denied forever. But a
// SINGLE denial is not revocation: Firestore rules redeploys — which happen
// several times a night here — can surface one-off permission-denied reads,
// and the instant negative then cleared the confirmed-workspace cache and
// flipped the session identity to null. Downstream, a null workspace UNFILTERS
// the thread rail (every org's threads flood in), lets the reconciler drop
// local threads at full authority, and re-stamps drafts on recovery — the
// owner-reported "threads keep disappearing and new threads keep appearing".
//
// Doctrine now: revocation is PERSISTENT denial. The first denial(s) hold the
// last confirmed identity exactly like a transient error (loudly); only
// denials that persist across a re-verification window (≥2 samples spanning
// ≥60s — the caller's retry backoff guarantees samples) become the
// authoritative negative with the original cache-clearing behavior. Server
// rules keep enforcing access regardless — this only decides how fast the
// CLIENT surface locks, and ~a minute of held (but unreadable) UI beats
// destroying a live member's workspace identity on every rules deploy.
// ─────────────────────────────────────────────────────────────────────────────

const PERMISSION_DENIED_AUTHORITATIVE_MIN_COUNT = 2;
export const PERMISSION_DENIED_AUTHORITATIVE_MIN_SPAN_MS = 60_000;

/** key → first-seen + count of consecutive permission-denied outcomes. */
const permissionDeniedWindows = new Map<string, { firstAtMs: number; count: number }>();

function recordPermissionDenied(key: string, nowMs: number = Date.now()): boolean {
  const existing = permissionDeniedWindows.get(key);
  if (!existing) {
    permissionDeniedWindows.set(key, { firstAtMs: nowMs, count: 1 });
    return false;
  }
  existing.count += 1;
  return (
    existing.count >= PERMISSION_DENIED_AUTHORITATIVE_MIN_COUNT &&
    nowMs - existing.firstAtMs >= PERMISSION_DENIED_AUTHORITATIVE_MIN_SPAN_MS
  );
}

function clearPermissionDeniedWindow(key: string): void {
  permissionDeniedWindows.delete(key);
}

/** Test hook — the window map is module state. */
export function resetPermissionDeniedWindowsForTests(): void {
  permissionDeniedWindows.clear();
}

// ─────────────────────────────────────────────────────────────────────────────
// Core gate function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines whether the given account should use the Prest0nRuntime path.
 *
 * Returns isMigrated: true only when ALL of:
 *   1. accountId is in the hardcoded MIGRATED_ACCOUNT_IDS set, OR
 *      Firestore config has migrationEnabled === true
 *   2. accounts/{accountId}/config/runtime exists with migrationEnabled: true
 *   3. accounts/{accountId}/vm/main.assigned === true
 *
 * Also resolves the AccountResolution so callers can reuse it without a second Firestore call.
 */
export async function shouldUsePrest0nRuntime(
  accountId: string
): Promise<MigrationGateResultWithResolution> {
  const strictFernando = isFernandoStrictMode(accountId);
  const notMigrated = (reason: string): MigrationGateResultWithResolution => {
    // Explicit (non-error) negative gate result — authoritative downgrade,
    // so the sticky-positive cache must not resurrect it on a later error.
    forgetMigrated(accountId);
    return {
      isMigrated: false,
      accountId,
      reason,
      resolution: null,
      authoritative: true,
    };
  };

  if (!accountId) return notMigrated('No accountId provided');

  try {
    const db = getFirestore(getApp());

    // ── Gate 0: global emergency kill switch ───────────────────────────────────
    // system/prest0Core/config.vmRoutingEnabled = false disables all VM routing
    try {
      const globalRef = doc(db, 'system', 'prest0Core', 'config', 'main');
      const globalSnap = await getDoc(globalRef);
      if (globalSnap.exists()) {
        const globalData = globalSnap.data();
        if (globalData?.vmRoutingEnabled === false) {
          return notMigrated('VM routing globally disabled (system/prest0Core/config/main.vmRoutingEnabled=false)');
        }
      }
    } catch {
      // Non-fatal — if the global config can't be read, proceed normally
    }

    // ── Gate 1: ONLY an explicit migrationEnabled === false is a negative ──────
    // R19-gate (INCIDENT-20260818): a missing config doc and a missing flag
    // are MIGRATED — the org-creation contract deliberately writes neither
    // ("a created org is structurally serverless"), and requiring them sent
    // every new org to the abandoned legacy in-browser orchestrator. The
    // explicit-false branch is preserved so the lifecycle stop-flip keeps
    // working; the allowlist may still override it (seeding edge cases).
    const configRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'config', 'runtime');
    const configSnap = await getDoc(configRef);
    if (configSnap.exists()) {
      const configData = configSnap.data();
      if (configData?.migrationEnabled === false && !MIGRATED_ACCOUNT_IDS.has(accountId)) {
        return notMigrated('migrationEnabled is explicitly false');
      }
    }

    // Gate 2 (vm/main.assigned|enabled) is GONE — R19-gate: vm/main is a
    // capability marker consumed by the server's execution router, not lane
    // identity. Requiring it here contradicted the 2026-08-12 universal
    // serverless-first doctrine and is exactly what de-migrated the sweep-
    // constituted org (which has no VM by design).

    // ── Gates passed — resolve account ─────────────────────────────────────────
    rememberMigrated(accountId);
    clearPermissionDeniedWindow(`acct:${accountId}`);
    const resolution = await resolveAccount(accountId);

    return {
      isMigrated: true,
      accountId,
      reason: 'All migration gates passed',
      resolution,
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    fernandoTrace(
      'MIGRATION_GATE_ERROR',
      {
        accountId,
        error: errorMessage,
        code: FERN_FE_CODES.MIGRATION_GATE_REJECTED,
      },
      strictFernando,
    );

    if (strictFernando && MIGRATED_ACCOUNT_IDS.has(accountId)) {
      fernandoTrace(
        'MIGRATION_GATE_BYPASS',
        {
          accountId,
          reason: 'strict Fernando allowlist bypass after Firestore client error',
        },
        true,
      );
      return {
        isMigrated: true,
        accountId,
        reason: `Strict Fernando allowlist bypass after gate error: ${errorMessage}`,
        resolution: null,
      };
    }

    // Finding 6 (2026-07-15) + debounce (2026-08-20): permission-denied is
    // Firestore's authoritative "this principal may not read these docs"
    // answer — the offboarding/revocation shape — but a SINGLE denial can
    // also be a rules-redeploy blip. Only PERSISTENT denial (the debounce
    // window above) downgrades authoritatively and clears the sticky entry;
    // the first denial(s) fall through to the transient-error ladders below,
    // loudly.
    const errorCode = (err as { code?: unknown } | null)?.code;
    if (errorCode === 'permission-denied') {
      if (recordPermissionDenied(`acct:${accountId}`)) {
        forgetMigrated(accountId);
        console.error(
          `[migrationGate] Gate check PERMISSION-DENIED for account ${accountId} — persistent, authoritative negative, sticky cache cleared`,
        );
        return {
          isMigrated: false,
          accountId,
          reason: `Migration gate permission-denied (authoritative negative): ${errorMessage}`,
          resolution: null,
          authoritative: true,
        };
      }
      console.error(
        `[migrationGate] Gate check permission-denied for account ${accountId} — holding through the debounce window (rules-deploy blip shape); persistent denial will downgrade`,
      );
      // Falls through to the sticky/default-migrated ladders below.
    }

    // Round J B1: sticky-positive on READ ERROR — never downgrade a
    // previously-migrated account because Firestore momentarily failed.
    if (wasMigratedLastKnown(accountId)) {
      console.error(
        `[migrationGate] Gate check FAILED for previously-migrated account ${accountId} — returning sticky isMigrated=true (error: ${errorMessage})`,
      );
      return {
        isMigrated: true,
        accountId,
        reason: `Sticky last-known-migrated after gate error: ${errorMessage}`,
        resolution: null,
      };
    }

    // R19-gate (INCIDENT-20260818): under the default-migrated contract a
    // generic read error must not route to the legacy orchestrator — it is
    // the strictly-worse, unmaintained lane. Default migrated, loudly;
    // resolution:null is an established return shape (the sticky path has
    // shipped it since Round J) and callers tolerate it.
    console.error(
      `[migrationGate] Gate check errored for account ${accountId} — defaulting to migrated (error: ${errorMessage})`,
    );
    return {
      isMigrated: true,
      accountId,
      reason: `Default-migrated after gate error: ${errorMessage}`,
      resolution: null,
    };
  }
}

/**
 * Convenience wrapper: resolves auth UID → accountId, then runs migration gate.
 * Use this when you only have the Firebase auth UID.
 */
export async function shouldUsePrest0nRuntimeForUser(
  authUid: string
): Promise<MigrationGateResultWithResolution> {
  if (!authUid) {
    return { isMigrated: false, accountId: null, reason: 'No auth UID', resolution: null, authoritative: true };
  }

  // Phase 1: accountId === authUid for direct accounts, but check membership first.
  //
  // R30.1 (2026-08-18): resolution runs STRICT so a transient read error is
  // distinguishable from the authoritative "no account" null. The old
  // catch-to-null resolver made a Firestore blip route a migrated account down
  // the legacy orchestrator lane with the Round J B1 sticky shield never
  // consulted (it only guarded shouldUsePrest0nRuntime's own reads, and this
  // wrapper bailed before reaching it).
  let resolution: AccountResolution | null;
  try {
    resolution = await resolveAccountStrict(authUid);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    // Finding 6 + debounce (2026-08-20, live rail-churn incident): only
    // PERSISTENT permission-denied is the authoritative revocation answer.
    // The instant negative here used to clear the confirmed-workspace cache
    // on a single rules-redeploy blip — the exact cache that holds a live
    // member's identity steady — and the null identity then inverted the
    // thread rail (foreign threads flood in, local threads dropped at full
    // reconcile authority). First denial(s) hold via the ladders below.
    if ((err as { code?: unknown } | null)?.code === 'permission-denied') {
      if (recordPermissionDenied(`uid:${authUid}`)) {
        forgetMigrated(authUid);
        // ORG STICKINESS: PERSISTENT revocation kills the confirmed-workspace
        // cache too.
        clearConfirmedWorkspace(authUid);
        clearTabWorkspacePin();
        console.error(
          `[migrationGate] Account resolution PERMISSION-DENIED for ${authUid} — persistent, authoritative negative, caches cleared`,
        );
        return {
          isMigrated: false,
          accountId: authUid,
          reason: `Account resolution permission-denied (authoritative negative): ${errorMessage}`,
          resolution: null,
          authoritative: true,
        };
      }
      console.error(
        `[migrationGate] Account resolution permission-denied for ${authUid} — holding through the debounce window (rules-deploy blip shape); persistent denial will downgrade`,
      );
      // Falls through to the confirmed-workspace / sticky hold ladders below.
    }
    // ORG STICKINESS (INCIDENT-20260818, Justin's wedged boot): these error
    // paths used to return `accountId: authUid` — the member's PERSONAL org —
    // as the session identity. The thread filter re-filtered to it (read as a
    // spontaneous org switch) and a thread created in that page load was
    // PERMANENTLY stamped into a view the member never opens. A transient
    // failure now keeps the user in the LAST CONFIRMED workspace; with no
    // cache, identity is explicitly UNRESOLVED and the caller holds.
    // (PR #925 reconciliation 2026-08-19: main's confirmed-workspace hold
    // supersedes this branch's parallel getLastWorkspace fallback.)
    const confirmed = readConfirmedWorkspace(authUid);
    if (wasMigratedLastKnown(authUid)) {
      if (confirmed) {
        console.error(
          `[migrationGate] Account resolution FAILED for previously-migrated user ${authUid} — holding last CONFIRMED workspace ${confirmed.accountId} (error: ${errorMessage})`,
        );
        return {
          isMigrated: true,
          accountId: confirmed.accountId,
          accountIdSource: 'cached-explicit',
          reason: `Sticky last-known-migrated; last confirmed workspace after account-resolution error: ${errorMessage}`,
          resolution: null,
        };
      }
      console.error(
        `[migrationGate] Account resolution FAILED for previously-migrated user ${authUid} — no confirmed workspace cached; identity UNRESOLVED (error: ${errorMessage})`,
      );
      return {
        isMigrated: true,
        accountId: null,
        accountIdSource: 'none',
        reason: `Sticky last-known-migrated; identity unresolved after account-resolution error: ${errorMessage}`,
        resolution: null,
      };
    }
    if (confirmed) {
      // Not sticky-known, but a workspace WAS confirmed for this uid before —
      // a confirmed workspace implies a previously working migrated session.
      console.error(
        `[migrationGate] Account resolution FAILED for ${authUid} — holding last CONFIRMED workspace ${confirmed.accountId} (error: ${errorMessage})`,
      );
      return {
        isMigrated: true,
        accountId: confirmed.accountId,
        accountIdSource: 'cached-explicit',
        reason: `Last confirmed workspace after account-resolution error: ${errorMessage}`,
        resolution: null,
      };
    }
    return {
      isMigrated: false,
      accountId: authUid,
      reason: `Account resolution failed with error: ${errorMessage}`,
      resolution: null,
      // Error-shaped negative: the consumer may HOLD a live identity through
      // it (2026-08-20) — only authoritative negatives may lower one.
      authoritative: false,
    };
  }
  // Any successful strict resolution (including the authoritative null below,
  // where every read succeeded) ends a permission-denied streak.
  clearPermissionDeniedWindow(`uid:${authUid}`);
  if (!resolution) {
    // Authoritative null: every read succeeded and no membership/profile doc
    // exists — a genuinely non-migrated user, so the stale sticky entry (if
    // any) must not resurrect on a later error.
    forgetMigrated(authUid);
    clearConfirmedWorkspace(authUid);
    clearTabWorkspacePin();
    return {
      isMigrated: false,
      accountId: authUid,
      reason: 'Account not in migrated set',
      resolution: null,
      authoritative: true,
    };
  }

  // PER-TAB ORGANIZATION (owner report 2026-09-11; D4's "the global pointer is
  // only a default, never an authority"). `resolution` above is the ACCOUNT-WIDE
  // pointer — shared by every device and tab, and moved out from under this page
  // whenever any other device switches. Ask the ladder what THIS TAB is in:
  // its own pin, else this device's recent workspace, else the pointer.
  const ladder = resolveTabWorkspace({
    uid: authUid,
    accountFromPointer: resolution.accountId,
  });

  if (ladder.accountId && ladder.accountId !== resolution.accountId) {
    // The ladder names a workspace the pointer does not. Prove membership the
    // way the rules do — accounts/{id}/profile/main is member-readable — before
    // letting it hold the view, so a pin for an org the user was removed from
    // can never strand them on a page that only produces denials.
    try {
      const pinned = await resolveNamedWorkspaceStrict(authUid, ladder.accountId);
      if (pinned) {
        const pinnedGate = await shouldUsePrest0nRuntime(pinned.accountId);
        if (pinnedGate.isMigrated) {
          rememberMigrated(authUid);
          writeConfirmedWorkspace(authUid, pinned.accountId);
          writeTabWorkspacePin(pinned.accountId, authUid);
          return {
            ...pinnedGate,
            accountIdSource: 'tab-pin',
            resolution: pinnedGate.resolution || pinned,
          };
        }
      }
      // Authoritative "not a member of that workspace any more" — drop the pin
      // and fall through to the pointer below (E12's safety fallback).
      clearTabWorkspacePin();
    } catch {
      // A transient read failure is NOT a revocation (INCIDENT-20260818): keep
      // the pin for the next boot and serve the pointer for now.
    }
  }

  const gate = await shouldUsePrest0nRuntime(resolution.accountId);
  if (gate.isMigrated) {
    // Mirror the sticky entry under the AUTH uid as well: the gate remembers
    // the RESOLVED account id, which for a firm MEMBER differs from their uid —
    // and the resolution failure path above can only consult the uid key.
    rememberMigrated(authUid);
    // ORG STICKINESS: a successful strict resolution is the ONLY writer of
    // the confirmed-workspace cache the error paths above trust.
    writeConfirmedWorkspace(authUid, resolution.accountId);
    // This tab has now settled here — pin it, so a switch on another device
    // cannot move this page's organization out from under it.
    writeTabWorkspacePin(resolution.accountId, authUid);
  }
  return {
    ...gate,
    accountIdSource: 'resolved',
    resolution: gate.resolution || resolution,
  };
}
